import { environment } from '@y42/environment';
import { AbstractStateService, getSimpleStateType, SimpleGenericStore } from '@y42/state';
import { createClient, Entry } from 'contentful';

export enum ContentfulGroupByTypesEnum {
  ManipulationType = 'manipulationType',
  ColumnType = 'columnType',
}

export abstract class AbstractContentfulStateService<R, T> extends AbstractStateService<SimpleGenericStore<T[]>> {
  protected constructor(
    public readonly contentType: string,
    public readonly order: string,
    public readonly limit: number,
    public readonly entityMapResponseFn: (res: Entry<R>) => getSimpleStateType<SimpleGenericStore<T>>,
    public readonly includeLevel: number = 1, // Level of nested linked assets and entries
    public override readonly initialState: SimpleGenericStore<T[]> = {
      entityState: [],
      loaded: false,
      loading: true,
    },
  ) {
    super(initialState, `contentful-${contentType}`);
    setTimeout(() => this.refreshContentfulEntries());
  }

  private lastFetched: Date | null = null;

  protected client = createClient(environment.contentful.datosStoreSpace);

  readonly data$ = this.select((st) => st.entityState);
  readonly loading$ = this.select((st) => st.loading);

  async refreshContentfulEntries(): Promise<void> {
    if (this.shouldInvoke()) {
      this.lastFetched = new Date();

      this.setState({ loading: true });
      const res = await this.client.getEntries<R>({
        content_type: this.contentType,
        order: this.order,
        limit: this.limit,
        include: this.includeLevel,
      });

      const entities: Array<getSimpleStateType<SimpleGenericStore<T>>> = res.items.map((contentfulModel: Entry<R>) =>
        this.entityMapResponseFn(contentfulModel),
      );

      if (entities.length === 0) {
        this.lastFetched = null;
      }

      this.setState({
        entityState: Object.freeze(entities) as T[],
        loaded: true,
        loading: false,
      });
    }
  }

  getData() {
    return this.state.entityState;
  }

  private shouldInvoke() {
    const MIN_TIME_BETWEEN_CALLS = 60 * 60 * 1000; // one hour

    const now = new Date();
    return !this.lastFetched || Math.abs(this.lastFetched.getTime() - now.getTime()) > MIN_TIME_BETWEEN_CALLS;
  }
}
